import * as React from 'react';
import { useState } from 'react';
import { Button, makeStyles, mergeClasses, Spinner, tokens } from '@fluentui/react-components';
import { AuthUser, generateMagicLink, refreshTokens } from '../../services/auth';
import {
  createDraftFromDocument,
  getDocumentBlob,
  getDocumentFilename,
} from '../../services/wordDraft';
import { appUrl } from '../../services/navigation';

/* global Office, window */

const SIGNUS_BLUE = '#1014f5';
const SIGNUS_BLUE_HOVER = '#2a2eff';
const SIGNUS_BLUE_PRESSED = '#0a0dcc';

const REVIEW_PURPLE = '#513476';
const REVIEW_PURPLE_HOVER = '#6a4a98';
const REVIEW_PURPLE_PRESSED = '#3c235a';

function openExternal(url: string): void {
  try {
    if (typeof Office !== 'undefined' && Office.context?.ui?.openBrowserWindow) {
      Office.context.ui.openBrowserWindow(url);
      return;
    }
  } catch {
    /* fall through to window.open */
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
  },
  btn: {
    minWidth: 'auto',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderRadius: '8px',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase200,
    '& *': { whiteSpace: 'nowrap' },
  },
  sendBtn: {
    backgroundColor: SIGNUS_BLUE,
    border: `1px solid ${SIGNUS_BLUE}`,
    color: '#ffffff',
    ':hover': {
      backgroundColor: SIGNUS_BLUE_HOVER,
      border: `1px solid ${SIGNUS_BLUE_HOVER}`,
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: SIGNUS_BLUE_PRESSED,
      border: `1px solid ${SIGNUS_BLUE_PRESSED}`,
      color: '#ffffff',
    },
  },
  reviewBtn: {
    backgroundColor: REVIEW_PURPLE,
    border: `1px solid ${REVIEW_PURPLE}`,
    color: '#ffffff',
    ':hover': {
      backgroundColor: REVIEW_PURPLE_HOVER,
      border: `1px solid ${REVIEW_PURPLE_HOVER}`,
      color: '#ffffff',
    },
    ':hover:active': {
      backgroundColor: REVIEW_PURPLE_PRESSED,
      border: `1px solid ${REVIEW_PURPLE_PRESSED}`,
      color: '#ffffff',
    },
  },
  errorText: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorPaletteRedForeground1,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface TopActionBarProps {
  user: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
}

const TopActionBar: React.FC<TopActionBarProps> = ({ user, onAuthChange }) => {
  const styles = useStyles();
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const handleSendForSignature = async () => {
    // If not authenticated, send to login
    if (!user) {
      openExternal(appUrl('login'));
      return;
    }

    setSending(true);
    setSendError(null);
    try {
      const freshUser = await refreshTokens(user, onAuthChange);
      if (!freshUser) {
        setSendError('Session expired. Please log in again.');
        return;
      }
      const [blob, filename] = await Promise.all([getDocumentBlob(), getDocumentFilename()]);
      const { documentId } = await createDraftFromDocument(
        blob,
        filename,
        freshUser.teamSlug,
        freshUser.accessToken,
      );
      const destination = appUrl(
        `${encodeURIComponent(freshUser.teamSlug)}/documents/all?addDocument=true&ctx=standard-drafts&documentId=${encodeURIComponent(documentId)}&step=2`,
      );
      // Generate a magic link so the user is auto-authenticated in the browser
      // when they open the link, then land directly on the recipients step.
      const magicLinkUrl = await generateMagicLink(freshUser.accessToken);
      openExternal(`${magicLinkUrl}?returnUrl=${encodeURIComponent(destination)}`);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Could not send document.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.bar}>
      <Button
        className={mergeClasses(styles.btn, styles.sendBtn)}
        onClick={handleSendForSignature}
        disabled={sending}
        icon={sending ? <Spinner size="tiny" /> : undefined}
      >
        Send for signature
      </Button>
      {sendError && <span className={styles.errorText}>{sendError}</span>}
      <Button
        className={mergeClasses(styles.btn, styles.reviewBtn)}
        onClick={() => openExternal(appUrl(''))}
      >
        Attorney review
      </Button>
    </div>
  );
};

export default TopActionBar;
